/**
 * Page /urbanisme/documents — gestion des documents de référence (staff).
 * Le maire/manager met à disposition PLU, zonages, CERFA… : upload PDF ou image
 * (compressée côté client), type pré-défini ou libellé libre, lien Géoportail
 * optionnel, afficher/masquer. UrbanismeLayout, zéro window.confirm.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { UrbanismeLayout } from '@/components/UrbanismeLayout';
import { Card, Button, Badge, Alert, SectionEyebrow, Field, Input } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { UrbaDocument } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { FileText, AlertCircle, Upload, Trash2, Eye, EyeOff, ExternalLink, Plus, Download } from 'lucide-react';

const TYPES: { value: string; label: string }[] = [
  { value: 'plu', label: 'PLU / PLUi' },
  { value: 'carte_communale', label: 'Carte communale' },
  { value: 'zonage', label: 'Zonage' },
  { value: 'reglement', label: 'Règlement de zone' },
  { value: 'cerfa', label: 'Formulaire CERFA' },
  { value: 'notice', label: 'Notice / guide' },
  { value: 'autre', label: 'Autre (libellé libre)' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));

function fmtTaille(o: number | null): string {
  if (o == null) return '';
  if (o < 1024 * 1024) return `${Math.round(o / 1024)} Ko`;
  return `${(o / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function UrbanismeDocumentsPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [docs, setDocs] = useState<UrbaDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Formulaire d'ajout
  const [titre, setTitre] = useState('');
  const [typeDoc, setTypeDoc] = useState('plu');
  const [typeLibre, setTypeLibre] = useState('');
  const [lienGpu, setLienGpu] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setDocs((await api.urbanisme.listDocuments()).documents); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function publier() {
    if (titre.trim().length < 1) { setError('Titre requis'); return; }
    if (!file && !lienGpu.trim()) { setError('Joignez un fichier ou un lien Géoportail'); return; }
    setBusy(true); setError(null);
    try {
      const doc = await api.urbanisme.createDocument({
        titre: titre.trim(), typeDoc, typeLibre: typeDoc === 'autre' ? (typeLibre.trim() || null) : null,
        lienGpu: lienGpu.trim() || null,
      });
      if (file) {
        const compressed = await compressImageIfNeeded(file);
        await api.urbanisme.uploadDocumentFichier(doc.id, compressed);
      }
      setTitre(''); setTypeLibre(''); setLienGpu(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await reload();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Publication impossible'); }
    finally { setBusy(false); }
  }

  async function toggleAffiche(d: UrbaDocument) {
    try { await api.urbanisme.updateDocument(d.id, { titre: d.titre, typeDoc: d.typeDoc, typeLibre: d.typeLibre, lienGpu: d.lienGpu, affiche: !d.affiche }); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function supprimer(d: UrbaDocument) {
    const ok = await confirm({ title: 'Supprimer ce document ?', description: 'Il ne sera plus visible par les administrés.', danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try { await api.urbanisme.removeDocument(d.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function importerCerfa() {
    const cat = await api.urbanisme.cerfaCatalogue().catch(() => ({ cerfa: [] }));
    const liste = cat.cerfa.map((c) => `• ${c.titre} (CERFA ${c.numero})`).join('\n');
    const ok = await confirm({
      title: 'Importer les formulaires CERFA officiels',
      description: `Ces formulaires seront ajoutés sous forme de liens vers service-public.gouv.fr (toujours la dernière version) :\n\n${liste}\n\nLes formulaires déjà présents ne seront pas dupliqués.`,
      confirmLabel: 'Importer',
    });
    if (!ok) return;
    try {
      const res = await api.urbanisme.importCerfa();
      await reload();
      setError(null);
      setInfo(res.importes > 0 ? `${res.importes} formulaire(s) CERFA importé(s).` : 'Tous les formulaires CERFA étaient déjà présents.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <UrbanismeLayout active="documents">
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 animate-fade-in">
        <SectionEyebrow as="div">Documents de référence</SectionEyebrow>
        <div className="mt-1.5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">Documents mis à disposition</h1>
            <p className="mt-2 text-sm text-ink-muted">PLU, zonages, règlements, formulaires… consultables par tous les habitants depuis leur espace urbanisme.</p>
          </div>
          <Button variant="secondary" onClick={importerCerfa} className="flex-none">
            <Download className="h-4 w-4" /> Importer les CERFA officiels
          </Button>
        </div>

        {info && <Alert variant="success" className="mt-4">{info}</Alert>}
        {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}

        <Card variant="glass" padded className="mt-6">
          <h2 className="font-display text-base font-semibold text-ink mb-3">Ajouter un document</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Type" htmlFor="doc-type">
              <select id="doc-type" value={typeDoc} onChange={(e) => setTypeDoc(e.target.value)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-urba">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            {typeDoc === 'autre' && (
              <Field label="Libellé libre" htmlFor="doc-libre">
                <Input id="doc-libre" value={typeLibre} onChange={(e) => setTypeLibre(e.target.value)} placeholder="Ex. Orientation d'aménagement" />
              </Field>
            )}
          </div>
          <Field label="Titre" htmlFor="doc-titre" required>
            <Input id="doc-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Plan Local d'Urbanisme — règlement écrit 2024" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Fichier (PDF ou image, 25 Mo max)" htmlFor="doc-file">
              <input ref={fileRef} id="doc-file" type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-r-md file:border-0 file:bg-urba-tint file:px-3 file:py-2 file:text-sm file:text-urba-fg" />
              <p className="mt-1 text-2xs text-ink-dim">Les images sont compressées automatiquement avant l'envoi.</p>
            </Field>
            <Field label="Lien Géoportail de l'urbanisme (optionnel)" htmlFor="doc-gpu">
              <Input id="doc-gpu" value={lienGpu} onChange={(e) => setLienGpu(e.target.value)} placeholder="https://www.geoportail-urbanisme.gouv.fr/…" />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={publier} disabled={busy}><Plus className="h-4 w-4" /> {busy ? 'Publication…' : 'Publier le document'}</Button>
          </div>
        </Card>

        <Card variant="glass" padded className="mt-4">
          <h2 className="font-display text-base font-semibold text-ink mb-2">Documents publiés</h2>
          {loading ? (
            <p className="text-sm text-ink-muted">Chargement…</p>
          ) : docs.length === 0 ? (
            <p className="text-sm text-ink-muted">Aucun document publié pour l'instant.</p>
          ) : (
            <div className="divide-y divide-border">
              {docs.map((d) => (
                <div key={d.id} className={`flex items-center gap-3 py-3 ${d.affiche ? '' : 'opacity-60'}`}>
                  <div className="flex h-9 w-9 flex-none items-center justify-center rounded-r-lg bg-urba-tint text-urba-fg"><FileText className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium text-ink">{d.titre}</span>
                      <Badge variant="urba" size="sm">{d.typeDoc === 'autre' && d.typeLibre ? d.typeLibre : TYPE_LABEL[d.typeDoc] ?? d.typeDoc}</Badge>
                      {d.cerfaNumero && <Badge variant="tech" size="sm">CERFA {d.cerfaNumero}</Badge>}
                      {d.lienGpu && <a href={d.lienGpu} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-tech-fg"><ExternalLink className="h-3 w-3" /> Géoportail</a>}
                      {d.urlExterne && <a href={d.urlExterne} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-tech-fg"><ExternalLink className="h-3 w-3" /> service-public.gouv.fr</a>}
                    </div>
                    <div className="text-xs text-ink-dim">{d.aFichier ? `${d.mimeType?.includes('pdf') ? 'PDF' : 'Image'} · ${fmtTaille(d.tailleOctets)}` : d.urlExterne ? 'Lien officiel' : 'Lien externe seulement'}{d.affiche ? '' : ' · masqué'}</div>
                  </div>
                  <div className="flex flex-none items-center gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => toggleAffiche(d)} aria-label={d.affiche ? 'Masquer' : 'Afficher'}>
                      {d.affiche ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => supprimer(d)} className="text-ink-muted hover:text-urgent" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>
      {dialogElement}
    </UrbanismeLayout>
  );
}
